import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { supportApi, SupportTicketCategory, TICKET_CATEGORY_LABELS } from "@/lib/api/support";

const CATEGORIES: SupportTicketCategory[] = [
  "GENERAL_INQUIRY",
  "TECHNICAL_SUPPORT",
  "DATABASE_ACCESS",
  "ACCOUNT_ISSUE",
  "CATALOG_FEEDBACK",
  "OTHER",
];

const MIN_SUBJECT = 1;
const MIN_MESSAGE = 1;

export default function NewTicketPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [category, setCategory] = useState<SupportTicketCategory>("GENERAL_INQUIRY");
  const [subject, setSubject] = useState("");
  const [databaseName, setDatabaseName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(
    () =>
      subject.trim().length >= MIN_SUBJECT &&
      message.trim().length >= MIN_MESSAGE &&
      (category !== "DATABASE_ACCESS" || databaseName.trim().length > 0),
    [subject, message, category, databaseName]
  );

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const ticket = await supportApi.createMine({
        subject: subject.trim(),
        category,
        message: message.trim(),
        databaseName: category === "DATABASE_ACCESS" ? databaseName.trim() || undefined : undefined,
      });
      toast.success(`Ticket ${ticket.ticketNumber} submitted`);
      navigate(`/support/${ticket.id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to submit ticket"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Open a support ticket"
        description="Tell us what you need help with. A librarian will get back to you shortly."
        breadcrumbItems={[{ label: "Support", href: "/support" }, { label: "New ticket" }]}
        showSearch={false}
        titleSize="md"
      />

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="space-y-1.5">
            <Label htmlFor="support-category" required>
              What is this about?
            </Label>
            <Select value={category} onValueChange={(v) => setCategory(v as SupportTicketCategory)}>
              <SelectTrigger id="support-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {TICKET_CATEGORY_LABELS[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {category === "DATABASE_ACCESS" && (
            <div className="space-y-1.5">
              <Label htmlFor="support-database" required>
                Which database?
              </Label>
              <Input
                id="support-database"
                placeholder="e.g. ProQuest, JSTOR, EBSCO"
                value={databaseName}
                onChange={(e) => setDatabaseName(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="support-subject" required>
              Subject
            </Label>
            <Input
              id="support-subject"
              placeholder="Short summary"
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-message" required>
              Message
            </Label>
            <Textarea
              id="support-message"
              rows={6}
              placeholder="Describe your request. Please do not share passwords here."
              maxLength={5000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <span className="text-xs text-slate-500">{message.length}/5000 characters</span>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => navigate("/support")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Submit ticket
            </Button>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
